import { defaultTheme, themes } from "../../core/theme/themes";
import { DEFAULT_FONT_SCALE, resolveFontScale, } from "../../core/theme/fontScale";
const VALID_THEME_KEYS = new Set([
    "name",
    "colors",
    "typography",
    "spacing",
    "shadows",
    "radii",
    "transitions",
    "breakpoints",
]);
function darkenHex(hex, amount) {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
    if (!m)
        return hex;
    const n = parseInt(m[1], 16);
    const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
    const r = clamp(((n >> 16) & 0xff) * (1 - amount));
    const g = clamp(((n >> 8) & 0xff) * (1 - amount));
    const b = clamp((n & 0xff) * (1 - amount));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}
function toCssLength(value) {
    return typeof value === "number" ? `${value}px` : value;
}
function warnStrayThemeKeys(theme) {
    const stray = Object.keys(theme).filter((k) => !VALID_THEME_KEYS.has(k));
    if (stray.length > 0) {
        console.warn(`[Medos] Ignoring unknown theme key(s): ${stray.join(", ")}. ` +
            `Pass primaryColor / secondaryColor / borderRadius / fontFamily as ` +
            `top-level config options instead of nesting them inside "theme".`);
    }
}
function buildFlatPartial(config) {
    const { primaryColor, secondaryColor, borderRadius, fontFamily, fontSize } = config;
    const fontScale = resolveFontScale(fontSize);
    const hasFontScale = fontScale !== DEFAULT_FONT_SCALE;
    if (!primaryColor &&
        !secondaryColor &&
        !borderRadius &&
        !fontFamily &&
        !hasFontScale) {
        return undefined;
    }
    const partial = {};
    if (primaryColor || secondaryColor) {
        partial.colors = {};
        if (primaryColor) {
            partial.colors.primary = primaryColor;
            partial.colors.primaryHover = darkenHex(primaryColor, 0.12);
            partial.colors.primaryActive = darkenHex(primaryColor, 0.2);
        }
        if (secondaryColor) {
            partial.colors.secondary = secondaryColor;
            partial.colors.secondaryHover = darkenHex(secondaryColor, 0.12);
        }
    }
    if (fontFamily || hasFontScale) {
        partial.typography = {};
        if (fontFamily) {
            partial.typography.fontFamily = fontFamily;
            partial.typography.fontFamilyHeading = fontFamily;
        }
        if (hasFontScale) {
            partial.typography.fontScale = fontScale;
        }
    }
    if (borderRadius !== undefined && borderRadius !== "") {
        const r = toCssLength(borderRadius);
        partial.radii = { md: r, lg: r };
    }
    return partial;
}
function layerFlat(base, flat) {
    return {
        ...base,
        ...(flat.colors
            ? { colors: { ...(base.colors ?? {}), ...flat.colors } }
            : {}),
        ...(flat.typography
            ? { typography: { ...(base.typography ?? {}), ...flat.typography } }
            : {}),
        ...(flat.radii ? { radii: { ...(base.radii ?? {}), ...flat.radii } } : {}),
    };
}
function isFullTheme(theme) {
    return "name" in theme && "colors" in theme && "typography" in theme;
}
export function resolveWidgetTheme(config) {
    const base = config.theme;
    if (base && typeof base === "object") {
        warnStrayThemeKeys(base);
    }
    const flat = buildFlatPartial(config);
    if (!flat)
        return base;
    if (base === undefined)
        return flat;
    if (typeof base === "string") {
        return layerFlat(themes[base] ?? defaultTheme, flat);
    }
    if (isFullTheme(base))
        return layerFlat(base, flat);
    return layerFlat(base, flat);
}
